import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  RadioInputDiv,
  InputDiv,
  Card,
  Result,
  MathDiv,
  ActionButtonsRow,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  RadioWrapper,
  Select,
  AlertDiv,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { MdError } from 'react-icons/md'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'

const PHI_OPTIONS = [10, 12.5, 16, 20, 25]

export function ConcreteRebarFatigue() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(30)
  const [alphae, setAlphae] = useState(10)
  const [sectionType, setSectionType] = useState('rectangular')
  const [b, setB] = useState('')
  const [bf, setBf] = useState('')
  const [bw, setBw] = useState('')
  const [hf, setHf] = useState('')
  const [h, setH] = useState('')
  const [d, setD] = useState('')
  const [diameter, setDiameter] = useState('16')
  const [quantity, setQuantity] = useState('')
  const [maximumMoment, setMaximumMoment] = useState('')
  const [minimumMoment, setMinimumMoment] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-rebar-fatigue-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setAlphae(entry.alphae)
      setSectionType(entry.sectionType || 'rectangular')
      setB(entry.b)
      setBf(entry.bf)
      setBw(entry.bw)
      setHf(entry.hf)
      setH(entry.h)
      setD(entry.d)
      setDiameter(entry.diameter)
      setQuantity(entry.quantity)
      setMaximumMoment(entry.maximumMoment)
      setMinimumMoment(entry.minimumMoment)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value, options = {}) => {
    const { allowZero = false, isInteger = false } = options

    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (isInteger && !Number.isInteger(numValue)) {
      return 'Valor deve ser um número inteiro'
    }

    if (allowZero ? numValue < 0 : numValue <= 0) {
      return allowZero ? 'Valor deve ser maior ou igual a 0' : 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.alphae = validateField('alphae', alphae)
    newErrors.h = validateField('h', h)
    newErrors.d = validateField('d', d)

    if (!newErrors.d && !newErrors.h && Number(d) >= Number(h)) {
      newErrors.d = 'Altura útil deve ser menor que a altura total'
    }

    if (sectionType === 'rectangular') {
      newErrors.b = validateField('b', b)
    } else {
      newErrors.bf = validateField('bf', bf)
      newErrors.bw = validateField('bw', bw)
      newErrors.hf = validateField('hf', hf)

      if (!newErrors.bf && !newErrors.bw && Number(bf) < Number(bw)) {
        newErrors.bf = 'Largura da mesa deve ser maior ou igual à largura da alma'
      }
      if (!newErrors.hf && !newErrors.h && Number(hf) >= Number(h)) {
        newErrors.hf = 'Espessura da mesa deve ser menor que a altura total'
      }
    }

    newErrors.quantity = validateField('quantity', quantity, { isInteger: true })
    newErrors.maximumMoment = validateField('maximumMoment', maximumMoment, { allowZero: true })
    newErrors.minimumMoment = validateField('minimumMoment', minimumMoment, { allowZero: true })

    if (!newErrors.maximumMoment && !newErrors.minimumMoment && Number(maximumMoment) < Number(minimumMoment)) {
      newErrors.maximumMoment = 'Momento máximo deve ser maior ou igual ao momento mínimo'
    }

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null && error !== undefined)
    setIsFormValid(!hasErrors)
  }, [fck, alphae, sectionType, b, bf, bw, hf, h, d, quantity, maximumMoment, minimumMoment, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const isRectangular = sectionType === 'rectangular'

      const data = {
        fck,
        alphae,
        sectionType,
        h,
        d,
        diameter: Number(diameter),
        quantity: Number(quantity),
        maximumMoment,
        minimumMoment,
        ...(isRectangular ? { b } : { bf, bw, hf }),
      }

      setData(data)

      const response = await api.post('/concrete/rebar-fatigue', data)

      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        alphae,
        sectionType,
        b,
        bf,
        bw,
        hf,
        h,
        d,
        diameter,
        quantity,
        maximumMoment,
        minimumMoment,
        data,
        result: response.data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      alphae,
      sectionType,
      b,
      bf,
      bw,
      hf,
      h,
      d,
      diameter,
      quantity,
      maximumMoment,
      minimumMoment,
      isFormValid,
      data,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concreto fadiga da armadura.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFck(jsonData.fck)
        setAlphae(jsonData.alphae)
        setSectionType(jsonData.sectionType || 'rectangular')
        setB(jsonData.b)
        setBf(jsonData.bf)
        setBw(jsonData.bw)
        setHf(jsonData.hf)
        setH(jsonData.h)
        setD(jsonData.d)
        setDiameter(jsonData.diameter)
        setQuantity(jsonData.quantity)
        setMaximumMoment(jsonData.maximumMoment)
        setMinimumMoment(jsonData.minimumMoment)
        setIsFormValid(jsonData.isFormValid)
        setData(jsonData.data)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const hasResult = result && result !== '' && result.reinforcement
  const isFlangeBottom = data.sectionType === 't-invertido'
  const region = hasResult ? result.crackedSection.region : null

  let regionLabel = ''
  if (hasResult) {
    if (data.sectionType === 'rectangular') {
      regionLabel = 'Seção retangular.'
    } else if (region === 'flange') {
      regionLabel = isFlangeBottom
        ? 'Linha neutra dentro da mesa inferior (T invertido).'
        : 'Linha neutra dentro da mesa (seção T).'
    } else if (region === 'web') {
      regionLabel = isFlangeBottom
        ? 'Linha neutra dentro da alma (T invertido).'
        : 'Linha neutra dentro da alma (seção T).'
    } else {
      regionLabel = 'Seção retangular equivalente.'
    }
  }

  const phiCm = hasResult ? (Number(data.diameter) / 10).toFixed(2) : ''
  const xII = hasResult ? result.crackedSection.neutralAxisDepth.toFixed(2) : ''
  const iII = hasResult ? result.crackedSection.inertia.toFixed(2) : ''
  const utilizationPercent = hasResult ? (result.verification.utilization * 100).toFixed(1) : ''

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Fadiga da Armadura</span>
          </div>
          <h1>🔁 Fadiga da Armadura Longitudinal</h1>
          <p className="subtitle">
            Verificação da variação de tensão na armadura longitudinal de seções de concreto armado sob solicitação
            repetida
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>📐 Estádio II</Badge>
            <Badge>🔬 Método de Branson</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta verifica a fadiga da armadura longitudinal de seções de concreto armado, calculando a
              variação de tensão no aço entre os momentos máximo e mínimo atuantes e comparando com o limite admissível
              da bitola informada.
            </p>
            <p>
              O cálculo utiliza a seção fissurada no Estádio II (linha neutra e inércia obtidas pelo método de Branson)
              para determinar as tensões no concreto e no aço.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Estádio II</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Relação do Módulo de Elasticidade do Aço e do Concreto (α_e = Es/Ec)</span>
              <ValidatedInput
                name="alphae"
                type="number"
                value={alphae || ''}
                onChange={(event) => setAlphae(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <RadioInputDiv>
              <span>Formato</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="shape"
                    id="shape-rectangle"
                    value="rectangular"
                    checked={sectionType === 'rectangular'}
                    onChange={() => setSectionType('rectangular')}
                  />
                  <span>Retangular</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="shape"
                    id="shape-t"
                    value="t"
                    checked={sectionType === 't'}
                    onChange={() => setSectionType('t')}
                  />
                  <span>T</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="shape"
                    id="shape-t-invertido"
                    value="t-invertido"
                    checked={sectionType === 't-invertido'}
                    onChange={() => setSectionType('t-invertido')}
                  />
                  <span>T invertido</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            {sectionType === 'rectangular' ? (
              <InputDiv>
                <span>Largura (cm)</span>
                <ValidatedInput
                  name="b"
                  type="number"
                  value={b || ''}
                  onChange={(event) => setB(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            ) : (
              <>
                <p style={{ fontSize: '90%', opacity: 0.8 }}>
                  {sectionType === 't'
                    ? 'A mesa fica na face superior (comprimida).'
                    : 'A mesa fica na face inferior. A compressão continua sendo considerada na face superior.'}
                </p>
                <InputDiv>
                  <span>Largura da mesa (cm)</span>
                  <ValidatedInput
                    name="bf"
                    type="number"
                    value={bf || ''}
                    onChange={(event) => setBf(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Largura da Alma (cm)</span>
                  <ValidatedInput
                    name="bw"
                    type="number"
                    value={bw || ''}
                    onChange={(event) => setBw(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Espessura da mesa (cm)</span>
                  <ValidatedInput
                    name="hf"
                    type="number"
                    value={hf || ''}
                    onChange={(event) => setHf(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}
            <InputDiv>
              <span>Altura total (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura útil (cm) — distância do topo ao centro da armadura tracionada</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d || ''}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Armadura</h3>
            <InputDiv>
              <span>Bitola das barras longitudinais (mm)</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={diameter}
                  onChange={(event) => setDiameter(event.target.value)}
                  disabled={isLoading}
                >
                  {PHI_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      Ø {option}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Quantidade de barras</span>
              <ValidatedInput
                name="quantity"
                type="number"
                value={quantity || ''}
                onChange={(event) => setQuantity(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Momento Máximo (kN.cm)</span>
              <ValidatedInput
                name="maximumMoment"
                type="number"
                value={maximumMoment}
                onChange={(event) => setMaximumMoment(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Momento Mínimo (kN.cm)</span>
              <ValidatedInput
                name="minimumMoment"
                type="number"
                value={minimumMoment}
                onChange={(event) => setMinimumMoment(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath math={`A_s = ${roundToDecimals(result.reinforcement.area)} \\, \\text{cm}^2`} />
                <BlockMath math={`\\Delta \\sigma_s = ${result.stresses.steel.variation.toFixed(2)} \\, \\text{MPa}`} />
                <BlockMath
                  math={`\\Delta \\sigma_{s,lim} = ${result.verification.allowableVariation} \\, \\text{MPa}`}
                />
                {result.verification.passes ? (
                  <BlockMath
                    math={`\\Delta \\sigma_s \\leq \\Delta \\sigma_{s,lim} \\; \\Rightarrow \\; \\text{Aprovado} \\; (${utilizationPercent}\\%)`}
                  />
                ) : (
                  <AlertDiv>
                    <BlockMath
                      math={`\\Delta \\sigma_s > \\Delta \\sigma_{s,lim} \\; \\Rightarrow \\; \\text{Reprovado} \\; (${utilizationPercent}\\%)`}
                    />
                    <MdError />
                  </AlertDiv>
                )}
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Área de aço</h3>
              <MathDiv>
                <BlockMath math={`A_s = n \\cdot \\dfrac{\\pi \\cdot \\phi^2}{4}`} />
                <BlockMath
                  math={`A_s = ${data.quantity} \\cdot \\dfrac{\\pi \\cdot ${phiCm}^2}{4} = ${roundToDecimals(result.reinforcement.area)} \\, \\text{cm}^2`}
                />
              </MathDiv>
              <h3>Seção fissurada (Estádio II)</h3>
              <MathDiv>
                <div style={{ fontSize: '90%', opacity: 0.8 }}>
                  <p>
                    Linha neutra e inércia obtidas pelo método de Branson considerando a seção fissurada. {regionLabel}
                  </p>
                </div>
                <BlockMath math={`x_{II} = ${xII} \\, \\text{cm}`} />
                <BlockMath math={`I_{II} = ${iII} \\, \\text{cm}^4`} />
              </MathDiv>
              <h3>Tensões no concreto</h3>
              <MathDiv>
                <BlockMath math={`\\sigma_{c,max} = \\dfrac{M_{max} \\cdot x_{II}}{I_{II}} \\cdot 10`} />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{c,max} = \\dfrac{${data.maximumMoment} \\cdot ${xII}}{${iII}} \\cdot 10 = ${result.stresses.concrete.maximum.toFixed(3)} \\, \\text{MPa}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{c,max} = \\dfrac{${data.maximumMoment} \\cdot ${xII}}{${iII}} \\cdot 10 = \\cdots`}
                  />
                  <BlockMath math={`\\cdots = ${result.stresses.concrete.maximum.toFixed(3)} \\, \\text{MPa}`} />
                </MobileOnlyDiv>
                <BlockMath math={`\\sigma_{c,min} = \\dfrac{M_{min} \\cdot x_{II}}{I_{II}} \\cdot 10`} />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{c,min} = \\dfrac{${data.minimumMoment} \\cdot ${xII}}{${iII}} \\cdot 10 = ${result.stresses.concrete.minimum.toFixed(3)} \\, \\text{MPa}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{c,min} = \\dfrac{${data.minimumMoment} \\cdot ${xII}}{${iII}} \\cdot 10 = \\cdots`}
                  />
                  <BlockMath math={`\\cdots = ${result.stresses.concrete.minimum.toFixed(3)} \\, \\text{MPa}`} />
                </MobileOnlyDiv>
              </MathDiv>
              <h3>Tensões no aço</h3>
              <MathDiv>
                <BlockMath
                  math={`\\sigma_{s,max} = \\alpha_e \\cdot \\sigma_{c,max} \\cdot \\dfrac{d - x_{II}}{x_{II}}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{s,max} = ${data.alphae} \\cdot ${result.stresses.concrete.maximum.toFixed(3)} \\cdot \\dfrac{${data.d} - ${xII}}{${xII}} = ${result.stresses.steel.maximum.toFixed(2)} \\, \\text{MPa}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{s,max} = ${data.alphae} \\cdot ${result.stresses.concrete.maximum.toFixed(3)} \\cdot \\dfrac{${data.d} - ${xII}}{${xII}} = \\cdots`}
                  />
                  <BlockMath math={`\\cdots = ${result.stresses.steel.maximum.toFixed(2)} \\, \\text{MPa}`} />
                </MobileOnlyDiv>
                <BlockMath
                  math={`\\sigma_{s,min} = \\alpha_e \\cdot \\sigma_{c,min} \\cdot \\dfrac{d - x_{II}}{x_{II}}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{s,min} = ${data.alphae} \\cdot ${result.stresses.concrete.minimum.toFixed(3)} \\cdot \\dfrac{${data.d} - ${xII}}{${xII}} = ${result.stresses.steel.minimum.toFixed(2)} \\, \\text{MPa}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{s,min} = ${data.alphae} \\cdot ${result.stresses.concrete.minimum.toFixed(3)} \\cdot \\dfrac{${data.d} - ${xII}}{${xII}} = \\cdots`}
                  />
                  <BlockMath math={`\\cdots = ${result.stresses.steel.minimum.toFixed(2)} \\, \\text{MPa}`} />
                </MobileOnlyDiv>
                <BlockMath math={`\\Delta \\sigma_s = \\sigma_{s,max} - \\sigma_{s,min}`} />
                <BlockMath
                  math={`\\Delta \\sigma_s = ${result.stresses.steel.maximum.toFixed(2)} - ${result.stresses.steel.minimum.toFixed(2)} = ${result.stresses.steel.variation.toFixed(2)} \\, \\text{MPa}`}
                />
              </MathDiv>
              <h3>Verificação da fadiga</h3>
              <MathDiv>
                <BlockMath
                  math={`\\Delta \\sigma_{s,lim} = ${result.verification.allowableVariation} \\, \\text{MPa} \\quad (\\phi ${data.diameter} \\, \\text{mm})`}
                />
                {result.verification.passes ? (
                  <BlockMath
                    math={`\\Delta \\sigma_s \\leq \\Delta \\sigma_{s,lim} \\; \\Rightarrow \\; ${result.stresses.steel.variation.toFixed(2)} \\leq ${result.verification.allowableVariation} \\; \\checkmark`}
                  />
                ) : (
                  <AlertDiv>
                    <BlockMath
                      math={`\\Delta \\sigma_s > \\Delta \\sigma_{s,lim} \\; \\Rightarrow \\; ${result.stresses.steel.variation.toFixed(2)} > ${result.verification.allowableVariation}`}
                    />
                    <MdError />
                  </AlertDiv>
                )}
                <BlockMath
                  math={`\\text{Utilização} = \\dfrac{\\Delta \\sigma_s}{\\Delta \\sigma_{s,lim}} = ${utilizationPercent}\\%`}
                />
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
